import {
  useState,
  useCallback,
  SetStateAction,
  JSXElementConstructor,
  Key,
  ReactElement,
  ReactNode,
  ReactPortal,
  useEffect
} from 'react';
import {
  Drawer,
  TextField,
  Stack,
  Typography,
  Divider,
  Box,
  Grid,
  Tab,
  Tabs,
  Button,
  FormControl,
  MenuItem,
  Alert,
  Snackbar
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useForm, FormProvider, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import * as APIService from '@shared/services/service-handlers/PrivateService';
import DialogToolBar from './AdditionData/DialogToolBar';
import {CustomFile, UploadProps} from 'src/minimal-components/upload';
import {
  RHFCheckbox,
  RHFSelect,
  RHFSingleUpload
} from 'src/minimal-components/hook-form';
import Resources from './AdditionData/Resources';
import NewFilesLockProperties from './AdditionData/NewFilesLockProperties';
import {fileDatas} from 'src/minimal-components/file-thumbnail';
import EncryptUtils from 'src/utils/EncryptUtils';
import uuidv4 from 'src/utils/uuidv4';
import {useAppContext} from '@context/AppContext';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {enqueueSnackbar} from 'notistack';
import {LoadingButton} from '@mui/lab';
import SelectInput from '@components/form/select-input/SelectInput';
import {FormControlDiv} from '../../module-search-filter/ModuleSearchFilterStyle';
import {LoadingScreen} from '@components/loading-screen';
import {BASE_RECORD_ID, LMS_IS_ADMIN} from 'src/routes/BaseRoute';
import {useModulePreviewContext} from '@context/ModulePreviewContextProvider';

interface ModuleData {
  id: string;
  name: string;
  expanded: boolean;
  description?: string;
  duration?: string;
  type: string;
  sequence: number;
  module_lock_type_id?: number;
  locked_until_date?: string;
  Module_Completed?: boolean;
  IsUnlocked?: boolean;
  is_completed?: boolean;
  locked_linked_items?: {
    id: string;
    item_name: string;
    entity_completion_sub_type_id: number;
    module_link_id: number;
    module_id: number;
    type: 'file' | 'link' | 'content' | 'assignment' | 'onlinetest' | 'module';
  }[];
  files?: {
    id: string;
    name: string;
    ParentName: string;
    blob_file_extension?: string;
    fileexpanded: boolean;
    time?: string;
    score?: string;
    lock_type_id: number;
    locked_until_date?: string;
    type: string;
    module_id: number;
    sequence: number;
    ChildItems_Completed?: boolean;
    IsUnlocked?: boolean;
    is_completed?: boolean;
    additionalFiles?: {id: string; name: string}[];
    locked_linked_items?: {
      id: string;
      item_name: string;
      entity_completion_sub_type_id: number;
      module_link_id: number;
      module_id: number;
      type:
        | 'file'
        | 'link'
        | 'content'
        | 'assignment'
        | 'onlinetest'
        | 'module';
    }[];
  }[];
}
const StyledLabel = styled('span')(({theme}) => ({
  ...theme.typography.caption,
  fontSize: '0.9rem',
  flexShrink: 0,
  color: theme.palette.text.secondary,
  fontWeight: theme.typography.fontWeightSemiBold
}));
// Validation schema using yup
const validationSchema = yup.object().shape({
  taskName: yup.string().required('Assignment name is required'),
  MaximumMarks: yup.number().required('Maximum marks are required'),
  SubmissionType: yup.number().required(),
  description: yup.string().notRequired(),
  MarkMandatory: yup.boolean().nullable().notRequired()
});

interface IKanbanTask {
  id: string;
  name: string;
  MaximumMarks: number;
  SubmissionType: number;
  description?: string;
  MarkMandatory?: boolean;
}
interface Propsnew extends Omit<UploadProps, 'file'> {
  multiple?: boolean;
}
interface DropDownData {
  Name: string;
  Values: {label: string; value: number}[];
}
interface Props extends Propsnew {
  task: any;
  uiFieldsdata: any;
  taskDone: any;
  // eslint-disable-next-line react/boolean-prop-naming
  openDetails: boolean;
  onCloseDetails: VoidFunction;
  onUpdateTask?: (updateTask: IKanbanTask) => void;
}

export default function AddAssignments({
  task,
  uiFieldsdata,
  openDetails,
  onCloseDetails,
  onUpdateTask,
  taskDone,
  ...other
}: Props) {
  const {
    appState: {applicationName}
  } = useAppContext();
  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );
  const lmsisAdmin = EncryptUtils.getDecryptedData(
    location?.search,
    LMS_IS_ADMIN
  );
  const {ModulePreviewData, SetModulePreviewData} = useModulePreviewContext();
  const [tabActive, setTabsActive] = useState<boolean>(true);
  // const [dropDownData, setDropDownData] = useState<DropDownData[]>([]);
  const {dropDownData, setDropDownData} = useModulePreviewContext();
  const [uiFields, setUiFields] = useState<any>(uiFieldsdata);
  const [insertedId, setInsertedId] = useState<any>();
  const [isloaded, setIsLoaded] = useState(false);
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      taskName: task?.data?.name || '',
      MaximumMarks: task?.data?.maximum_marks || undefined,
      SubmissionType: task?.data?.assignment_submission_type_id || 3,
      description: task?.data?.description || '',
      MarkMandatory: task?.data?.mark_as_mandatory || false
    }
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [assignmentloader, setAssignmentLoader] = useState(false);
  const [submissionType, setSubmissionType] = useState(3);
  const [saved, setSaved] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(0);
  const handleChange = (event: any, newValue: SetStateAction<number>) => {
    if (tabActive) {
      setOpenSnackbar(true); // Show the snackbar when `tabActive` is true
    } else {
      setActiveTab(newValue); // Switch tabs
    }
  };
  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
    setSaved(false);
  };
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {errors, isSubmitting}
  } = methods;

  const values = watch();
  const findFileInModule = (
    data: any[],
    moduleId: number,
    fileId: number,
    type: string
  ) => {
    const module = data.find(m => m.id == moduleId);
    if (!module) return null;
    const file = module.files?.find(
      (f: {id: number; type: string}) => f.id == fileId && f.type === type
    );
    return file ?? null;
  };
  // Handle removing all files
  const generateOptionsList = (fields: any, data: any) => {
    const transformedData = data
      .map((item: {name: any; id: any}) => ({
        label: item.name,
        value: item.id
      }))
      .sort(
        (a: {sequence: number}, b: {sequence: number}) =>
          a.sequence - b.sequence
      );

    return transformedData;
  };
  const handleLegacyAppChange = async (
    event: any,
    name: string,
    field?: any
  ) => {
    if (uiFields) {
      const {sql_data, order_by, sort_by} = uiFields[name];
      try {
        const data: any = {
          sql_data: sql_data,
          sort: order_by,
          sort_by: sort_by,
          isImportFiles: true
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await APIService.getSearchResultsData(
          applicationName,
          'LMS',
          {
            tk: encryptRequestData
          }
        );
        const new_options = generateOptionsList(
          [{generate_options: true}],
          result?.data
        );
        await setDropDownData((prevData: any[]) => {
          const existingEntry = prevData?.find(
            (entry: {Name: string}) => entry.Name === name
          );

          if (existingEntry) {
            // If the Name already exists, update its Values
            return [...prevData];
          }

          // If the Name is new, add it to the array
          return [...prevData, {Name: name, Values: new_options}];
        });
        return new_options;
      } catch (e) {
        const errMessage = getErrorMessage(e);
        enqueueSnackbar(errMessage, SnackBarConfig.getError());
      }
    } else {
      const {sql_data, order_by, sort_by} = field[name];
      try {
        const data: any = {
          sql_data: sql_data,
          sort: order_by,
          sort_by: sort_by,
          isImportFiles: true
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await APIService.getSearchResultsData(
          applicationName,
          'LMS',
          {
            tk: encryptRequestData
          }
        );
        const new_options = generateOptionsList(
          [{generate_options: true}],
          result?.data
        );
        setDropDownData((prevData: any[]) => {
          const existingEntry = prevData?.find(
            (entry: {Name: string}) => entry.Name === name
          );

          if (existingEntry) {
            // If the Name already exists, update its Values
            return [...prevData];
          }

          // If the Name is new, add it to the array
          return [...prevData, {Name: name, Values: new_options}];
        });
        return new_options;
      } catch (e) {
        const errMessage = getErrorMessage(e);
        enqueueSnackbar(errMessage, SnackBarConfig.getError());
      }
    }
  };
  const init = async () => {
    try {
      setAssignmentLoader(true);
      let res;
      if (uiFieldsdata === undefined) {
        res = await APIService.getSearchForm(
          applicationName,
          'module',
          'AddAssignments'
        );
        await setUiFields(res.searchForm);
        const hasEntityCompletionType = dropDownData.some(
          item => item.Name === 'SubmissionType'
        );

        if (!hasEntityCompletionType) {
          await handleLegacyAppChange(
            'SubmissionType',
            'SubmissionType',
            res.searchForm
          );
        }
      } else {
        setUiFields(uiFieldsdata);
        const hasEntityCompletionType = dropDownData.some(
          item => item.Name === 'SubmissionType'
        );

        if (!hasEntityCompletionType) {
          await handleLegacyAppChange(
            'SubmissionType',
            'SubmissionType',
            uiFieldsdata
          );
        }
      }
      const fileData = findFileInModule(
        ModulePreviewData,
        Number(insertedId?.data[0].module_id),
        Number(insertedId?.data[0].inserted_id),
        insertedId?.data[0].type
      );
      if (fileData) {
        setValue('taskName', fileData?.name);
        setValue('MaximumMarks', fileData?.maximum_marks);
        setValue('SubmissionType', fileData?.assignment_submission_type_id);
        setValue('description', fileData?.description);
        setValue('MarkMandatory', fileData?.mark_as_mandatory);
      } else {
        if (task?.data?.id) {
          setValue('taskName', task?.data?.name);
          setValue('MaximumMarks', task?.data?.maximum_marks);
          setValue('SubmissionType', task?.data?.assignment_submission_type_id);
          setValue('description', task?.data?.description);
          setValue('MarkMandatory', task?.data?.mark_as_mandatory);
        } else {
          if (
            (res?.searchForm || uiFieldsdata !== undefined) &&
            (insertedId || task?.inserted_id)
          ) {
            const {sql_data} =
              uiFieldsdata['create_sql'] || res.searchForm['create_sql'];
            let operations;
            if (insertedId || task?.inserted_id) {
              operations = 'SEARCH';
            }
            const data: any = {
              sql_data: sql_data,
              data: {
                id: insertedId?.data[0]?.inserted_id || task?.inserted_id,
                course_overview_id: uniqueId
              },
              operation: operations
            };
            const encryptRequestData = EncryptUtils.encryptedData(data);
            const result = await APIService.getLMSResultsData(
              applicationName,
              'LMS',
              {
                tk: encryptRequestData
              }
            );
            if (result.data[0].assignment_submission_type_id) {
              setSubmissionType(result.data[0].assignment_submission_type_id);
              setValue(
                'SubmissionType',
                result.data[0].assignment_submission_type_id
              );
              setValue('taskName', result.data[0].name);
              setValue('MaximumMarks', result.data[0].mark_as_mandatory);
              setValue('description', result.data[0].description);
              setValue('MaximumMarks', result.data[0].maximum_marks);
            }

            setAssignmentLoader(false);
          }
        }
      }

      setAssignmentLoader(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setAssignmentLoader(false);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      if (task) {
        setTabsActive(false);
      }
      if (taskDone?.operation === 'INSERT') {
        setInsertedId(undefined);
      } else if (insertedId !== undefined) {
        setInsertedId(insertedId);
      } else {
        setInsertedId({
          data: [
            {
              module_id: task?.module_id,
              inserted_id: task?.inserted_id,
              type: 'assignment'
            }
          ]
        });
      }
      await init();
    };

    fetchData();
  }, []);
  const onSubmit = async (datavalue: any) => {
    onUpdateTask?.({
      ...task,
      name: datavalue.taskName,
      SubmissionType: datavalue?.SubmissionType,
      MaximumMarks: datavalue.MaximumMarks,
      description: datavalue?.description,
      MarkMandatory: datavalue?.MarkMandatory
    });
    try {
      if (uiFields) {
        const {sql_data} = uiFields['create_sql'];
        let operations;
        if (taskDone?.id && taskDone?.operation) {
          operations = taskDone?.operation;
          datavalue.module_id = taskDone?.id;
        }
        if (insertedId && insertedId.data.length > 0) {
          operations = 'UPDATE';
          datavalue.id = insertedId.data[0].inserted_id;
          datavalue.course_overview_id = uniqueId;
        }
        datavalue.isAdmin = lmsisAdmin === 'True';
        datavalue.course_overview_id = uniqueId;
        datavalue.page = 1;
        // datavalue.bloburls = datavalue.bloBurls[0].blobUrl;
        setIsLoaded(true);
        const data: any = {
          sql_data: sql_data,
          data: datavalue,
          operation: operations
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await APIService.getLMSResultsData(
          applicationName,
          'LMS',
          {
            tk: encryptRequestData
          }
        );
        if (result.data[0]) {
          setInsertedId({data: [{...result.data[0], type: 'assignment'}]});
        }
        const processModulesData = (data: ModuleData[]) => {
          return data.map(module => ({
            ...module,
            files:
              module.files?.map(file => ({
                ...file,
                locked_linked_items:
                  typeof file.locked_linked_items === 'string'
                    ? JSON.parse(file.locked_linked_items) // 🛠️ Parse if string
                    : file.locked_linked_items || [], // Keep array or default to empty
                additionalFiles:
                  typeof file.additionalFiles === 'string'
                    ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
                    : file.additionalFiles || [] // Keep array or default to empty
              })) || []
          }));
        };
        const syncExpandedStates = (
          transformed: ModuleData[],
          existing: ModuleData[]
        ) => {
          return transformed.map(module => {
            const matchingModule = existing.find(e => e.id === module.id);

            if (!matchingModule) return module;

            // Sync the `expanded` flag
            const updatedModule = {
              ...module,
              expanded: matchingModule.expanded || false,
              files:
                module?.files &&
                module?.files.map(file => {
                  const matchingFile =
                    matchingModule?.files &&
                    matchingModule.files.find(f => f.id === file.id);
                  return {
                    ...file,
                    fileexpanded: matchingFile?.fileexpanded || false
                  };
                })
            };

            return updatedModule;
          });
        };

        // Handling API result
        if (result.data[1]) {
          const transformData = await processModulesData(result.data[1]);
          const updatedTransformed = syncExpandedStates(
            transformData,
            ModulePreviewData
          );
          await SetModulePreviewData(updatedTransformed);
        }
        setIsLoaded(false);
        setSaved(true);
        setTabsActive(false);
        setActiveTab(1);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoaded(false);
    }
    setTabsActive(false);
  };

  return (
    <FormProvider {...methods}>
      <Drawer
        open={openDetails}
        onClose={onCloseDetails}
        anchor="right"
        slotProps={{
          backdrop: {invisible: true}
        }}
        PaperProps={{
          sx: {
            width: {
              xs: 1,
              sm: '47vw'
            }
          }
        }}>
        <DialogToolBar
          title={task ? 'Edit Assignment' : 'Add Assignment'}
          onCloseDetails={onCloseDetails}
        />
        <Divider />
        <Box sx={{width: '100%'}}>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={2}>
            <Grid item xs={12}>
              <Tabs
                value={activeTab}
                onChange={handleChange}
                variant="fullWidth"
                textColor="primary"
                sx={{
                  backgroundColor: '#F4F6F8',
                  '& .MuiTab-root': {
                    fontSize: '16px',
                    fontWeight: '500',
                    '&:hover': {
                      backgroundColor: '#e0e0e0', // Change background on hover
                      borderRadius: 2
                    }
                  },
                  '& .MuiTab-root.Mui-selected': {
                    color: 'primary.main',
                    margin: 1,
                    backgroundColor: 'white',
                    borderRadius: 2
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#637381',
                    height: 0
                  }
                }}>
                <Tab label="Basic Info" />
                <Tab
                  label="Lock Properties"
                  disabled={assignmentloader || isloaded}
                />
                <Tab
                  label="Resources"
                  disabled={assignmentloader || isloaded}
                />
              </Tabs>
            </Grid>
          </Grid>
        </Box>
        {activeTab === 0 &&
          (assignmentloader ? (
            <Stack
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              minHeight={'50vh'}>
              <LoadingScreen />
            </Stack>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} style={{overflowY: 'auto'}}>
              <Stack spacing={1.5} sx={{pt: 3, pb: 5, px: 2.5}}>
                {/* Task Name */}
                <Stack spacing={2} direction="column">
                  <StyledLabel>Name</StyledLabel>
                  <Controller
                    name="taskName"
                    control={control}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        error={!!errors.taskName}
                        helperText={errors.taskName?.message}
                      />
                    )}
                  />
                </Stack>
                <Stack spacing={2} direction="column">
                  <StyledLabel>Maximum Marks</StyledLabel>
                  <Controller
                    name="MaximumMarks"
                    control={control}
                    rules={{
                      validate: value => value !== 0 || 'Duration cannot be 0' // Custom validation
                    }}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        type="number"
                        error={!!errors.MaximumMarks}
                        helperText={errors.MaximumMarks?.message}
                        onChange={event => {
                          const newValue = event.target.value;
                          if (newValue === '' || parseInt(newValue, 10) > 0) {
                            field.onChange(
                              newValue === '' ? null : parseInt(newValue, 10)
                            );
                          }
                        }}
                        value={field.value ?? ''}
                      />
                    )}
                  />
                </Stack>
                <FormControl margin="normal">
                  <Controller
                    name="SubmissionType"
                    control={control}
                    render={({field: {value, onChange}}) => (
                      <FormControlDiv>
                        <SelectInput
                          enableDefaultOption
                          name="SubmissionType"
                          options={
                            dropDownData.find(
                              item => item.Name === 'SubmissionType'
                            )?.Values || []
                          }
                          value={value || submissionType}
                          fullWidth
                          onOpenCallback={(event: any) =>
                            handleLegacyAppChange(event, 'SubmissionType')
                          }
                          onChange={(event: any) => {
                            onChange(event);
                            setSubmissionType(Number(event.target.value));
                            // setState(prevState => ({
                            //   ...prevState,
                            //   formError: ''
                            // }));
                          }}
                          optionValueKey={'value'}
                          optionLabelKey={'label'}
                          fetchOptionsFromOnOpenTrigger
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: '30vh', // Set dropdown max height
                                overflowY: 'auto' // Enable scrolling
                              }
                            }
                          }}
                        />
                      </FormControlDiv>
                    )}
                  />
                </FormControl>
                <Stack spacing={2} direction="column">
                  <StyledLabel>Description</StyledLabel>
                  <Controller
                    name="description"
                    control={control}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        size="small"
                        InputProps={{
                          sx: {
                            typography: 'body2',
                            minHeight: '12vh',
                            alignItems: 'baseline'
                          }
                        }}
                        sx={{minHeight: '12vh'}}
                        error={!!errors.description}
                        helperText={errors.description?.message}
                      />
                    )}
                  />
                </Stack>
                <Stack direction="row">
                  <RHFCheckbox
                    name="MarkMandatory"
                    label="Mandatory Assignment"
                  />
                </Stack>

                <Stack display="flex" flexDirection="row-reverse">
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="info"
                    loading={isloaded}
                    disabled={isSubmitting}
                    onClick={() => {
                      if (!errors) {
                        onSubmit;
                      }
                    }}>
                    Save
                  </LoadingButton>
                </Stack>
              </Stack>
            </form>
          ))}
        {activeTab === 1 && (
          <NewFilesLockProperties
            uiFields={uiFields}
            Moduledata={task?.data ? task.data : undefined}
            insertdata={insertedId}
            tableNameEntity="ModuleAssignmentSubList"
          />
        )}
        {activeTab === 2 && (
          <Resources
            uiFields={uiFields}
            Moduledata={task?.data ? task.data : undefined}
            insertdata={insertedId}
            tableNameEntity="ContentModuleResourseAssignMents"
          />
        )}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert
            onClose={handleSnackbarClose}
            severity="warning"
            sx={{width: '40vw'}}>
            Basic information data must be added before accessing other tabs.
          </Alert>
        </Snackbar>
        <Snackbar
          open={saved}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert
            onClose={handleSnackbarClose}
            severity="success"
            sx={{width: '40vw'}}>
            Basic Info is Saved
          </Alert>
        </Snackbar>
      </Drawer>
    </FormProvider>
  );
}
